// [2026-08-11] ADDED — the sign-in screen, rebuilt on shadcn as a two-panel layout.
//
// PRESENTATION ONLY. Every piece of behaviour — the role check that decides which door a user came
// through, the Google Identity Services lifecycle, the submit handler — stays in App.jsx's `Login`
// and arrives here as props. That split is deliberate: this file is the part that will be
// redesigned again, and a redesign must never be able to touch the authentication logic.
//
// LAYOUT: image left, form right, per the brief.
//   < 1024px   one column. The image panel is dropped entirely rather than stacked — on a phone it
//              would push the form below the fold, and the form is the reason for the page.
//   ≥ 1024px   two equal columns, both full height.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────
// PUTTING YOUR OWN IMAGE ON THE LEFT
// ─────────────────────────────────────────────────────────────────────────────────────────────
// Drop a file at  dashboard-react/src/assets/auth-cover.jpg  and rebuild (`npm run build`).
// That is the whole procedure — no code change, no server change.
//
// .jpg / .jpeg / .png / .webp all work; the NAME is what matters. Portrait or landscape is fine,
// at least 1200×1600, and choose something dark enough for white text: the wordmark sits top-left
// and the caption bottom-left, over a scrim.
//
// Until that file exists the panel renders a branded gradient. That is a finished state, not a
// placeholder to replace before shipping.
//
// WHY import.meta.glob AND NOT <img src="/static/auth-cover.jpg" onError={…}>
// The first version did exactly that, and it cost an afternoon: a missing optional image still
// issues a real request, so every page load logged a 404 to the console. Three e2e suites assert
// the console is clean — correctly, since that assertion is what catches genuine React errors —
// and all of them went red for a file that was never required. `onError` hides the broken image
// but cannot un-send the request.
//
// This glob is resolved by Vite AT BUILD TIME. No file, no import, no request, no 404. It also
// means the image is hashed and cache-busted like every other asset, which the public/ route
// would not have given us.

import { useState } from 'react'
import { Button } from '../ui/button.jsx'
import { Input } from '../ui/input.jsx'
import { Label } from '../ui/label.jsx'
import { Notice } from '../../ui.jsx'
import { cn } from '../../lib/utils.js'

const COVERS = import.meta.glob('../../assets/auth-cover.{jpg,jpeg,png,webp}', {
  eager: true, query: '?url', import: 'default',
})
const COVER_SRC = Object.values(COVERS)[0] || null

// [2026-08-27] CHANGED — the Client / InCruiter staff TOGGLE IS GONE.
//
// The two sign-ins are now separate pages at separate URLs (`/` and `/admin`), so this component
// renders exactly one door — whichever page it is on — and offers no way to reach the other. That
// is the whole change: a customer must never be shown a staff door.
//
// It also grew two more panels for the customer door, chosen by the `panel` prop:
//   'signin'       email + password, Google, and links to the two below
//   'forgot'       ask for a reset link
//   'forgot_sent'  the neutral confirmation (see the note where it renders)
//   'reset'        choose a new password, arrived at from `?reset_token=`
//
// The staff door renders ONLY 'signin', and the server refuses reset and Google for staff accounts
// regardless of what is rendered here.
//
// STILL PRESENTATION ONLY. Every handler, the door/role check and the Google lifecycle remain in
// App.jsx's `Login`. The rule from the original header holds: a redesign of this file must not be
// able to touch the authentication path.
export default function LoginScreen({
  door, meta, panel = 'signin', onPanelChange, notice,
  email, onEmailChange, password, onPasswordChange,
  confirmPassword, onConfirmPasswordChange,
  error, busy, onSubmit, onSubmitForgot, onSubmitReset,
  googleReady, googleRef, selfRegistration, passwordReset,
}) {
  const isAdminDoor = door === 'admin'
  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-2 bg-background">
      <CoverPanel />

      {/* ── the form column ─────────────────────────────────────────────────
          A whisper of tint at the top rather than flat --page. Beside a full-bleed image, a flat
          panel reads as an unstyled half of the screen; this gives it a light source without
          introducing a colour the design system does not already contain. */}
      <div className="flex items-center justify-center px-6 py-10 sm:px-10"
           style={{ background: 'linear-gradient(180deg, #ffffff 0%, var(--page) 55%)' }}>
        <div className="w-full max-w-[400px]">

          {/* THE WORDMARK BELONGS IN THIS COLUMN, NOT ON THE IMAGE PANEL — two reasons, and the
              first is a behaviour the test suite pins:

              1. It renders `meta.title`, which is the one place the chosen door is spelled out in
                 full ("InCruiter Proctoring" vs "… — Admin"). e2e/dashboard.spec.js asserts on
                 `.brand` to prove the door switch changes the SCREEN and not just a highlight. Put
                 this on the cover panel and that signal vanishes below 1024px, where the panel is
                 not rendered at all.
              2. The image panel is hidden on narrow screens, so a wordmark living there would
                 leave the mobile sign-in page with no product name — disorienting for someone
                 arriving from an emailed link.

              It is also where shadcn's own two-panel auth block puts it. */}
          <div className="brand mb-8 flex items-center gap-2.5" style={{ padding: 0 }}>
            <span className="brand-mark">In</span>
            <span className="text-[15px] font-semibold text-foreground">{meta.title}</span>
          </div>

          <h1 className="text-[30px] font-semibold tracking-[-0.02em] leading-tight text-foreground [font-family:inherit]">
            {meta.heading}
          </h1>
          <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">{meta.blurb}</p>

          {/* [2026-08-27] THE DOOR SWITCH WAS REMOVED HERE.
              --- OLD (pre-2026-08-27): a two-button segmented control over `doors` ---
              <div role="group" aria-label="Account type"
                   className="mt-7 grid grid-cols-2 gap-1 rounded-[var(--radius)] border ...">
                {Object.entries(doors).map(([key, value]) => {
                  const active = door === key
                  return (
                    <button key={key} type="button" aria-pressed={active}
                            onClick={() => onDoorChange(key)} className={cn(...)}>
                      {value.label}
                    </button>
                  )
                })}
              </div>
              --- END OLD ---

              WHY IT IS GONE: the customer and staff sign-ins are separate pages now, so there is
              nothing to switch between and a customer is never shown a staff door at all.

              THE aria-pressed LESSON FROM THAT CONTROL STILL APPLIES ELSEWHERE — `role="tab"`
              REPLACES a button's implicit role and broke every automated sign-in in the repo. If a
              toggle group is ever added to this page again, use `aria-pressed`, not `role="tab"`.

              The e2e suites that opened with `getByRole('button', { name: 'Client' })` must now
              navigate to the right URL instead. */}

          {/* [2026-08-27] ADDED — a non-error message: "check your inbox", "password updated".
              Uses the app's own Notice for the same reason the error below does — one presentation
              of a message across every screen, and one selector for a test to assert on. */}
          {/* kind="success" — the real class name in theme.css:328. `ok` would have rendered an
              unstyled `.notice ok` div: visible, but without the green treatment every other
              success message in the product uses. */}
          {notice && (
            <div role="status" className="mt-5">
              <Notice kind="success">{notice}</Notice>
            </div>
          )}

          {/* THE APP'S OWN Notice, not a hand-rolled div.
              A first draft styled this inline with Tailwind. It looked the same and was wrong
              twice over: it forked the error presentation away from the other eight screens, and
              it dropped the `.notice.error` hook that e2e/dashboard.spec.js uses to prove a bad
              password is actually reported. Reusing the component keeps one error style in the
              product and one selector to assert on. */}
          {error && (
            <div role="alert" className="mt-5">
              <Notice kind="error">{error}</Notice>
            </div>
          )}

          {/* ── panel: SIGN IN ────────────────────────────────────────────── */}
          {panel === 'signin' && (
            <form onSubmit={onSubmit} className="mt-6 grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="signin-email">Email</Label>
                <Input id="signin-email" type="email" required autoFocus autoComplete="username"
                       placeholder={isAdminDoor ? 'you@incruiter.com' : 'you@company.com'}
                       value={email} onChange={(e) => onEmailChange(e.target.value)} />
              </div>

              <div className="grid gap-2">
                {/* The label row carries "Forgot password?" so the link sits where someone looks
                    when the password is the thing they cannot supply — not buried under the
                    button. Customer door only: staff password changes are a deliberate operator
                    action, never a mailbox-driven one. */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="signin-password">Password</Label>
                  {passwordReset && !isAdminDoor && (
                    <button type="button" onClick={() => onPanelChange('forgot')}
                            className="text-[12.5px] font-medium text-muted-foreground
                                       hover:text-foreground cursor-pointer appearance-none
                                       border-0 bg-transparent p-0 [font-family:inherit]
                                       focus-visible:outline-none focus-visible:underline">
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input id="signin-password" type="password" required autoComplete="current-password"
                       placeholder="••••••••"
                       value={password} onChange={(e) => onPasswordChange(e.target.value)} />
              </div>

              {/* h-11 matches the inputs exactly, so the column reads as one stack rather than as a
                  form with a button bolted underneath. */}
              <Button type="submit" size="lg" disabled={busy}
                      className="mt-1 w-full h-11 text-[14px] font-semibold">
                {busy ? 'Signing in…' : `Sign in${isAdminDoor ? ' as staff' : ''}`}
              </Button>
            </form>
          )}

          {/* ── panel: FORGOT PASSWORD ────────────────────────────────────── */}
          {panel === 'forgot' && (
            <form onSubmit={onSubmitForgot} className="mt-6 grid gap-4">
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                Enter the email address you sign in with and we will send you a link to choose a
                new password.
              </p>
              <div className="grid gap-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input id="forgot-email" type="email" required autoFocus autoComplete="username"
                       placeholder="you@company.com"
                       value={email} onChange={(e) => onEmailChange(e.target.value)} />
              </div>
              <Button type="submit" size="lg" disabled={busy}
                      className="mt-1 w-full h-11 text-[14px] font-semibold">
                {busy ? 'Sending…' : 'Send reset link'}
              </Button>
              <BackToSignIn onPanelChange={onPanelChange} />
            </form>
          )}

          {/* ── panel: RESET LINK SENT ────────────────────────────────────────
              THE COPY HERE IS DELIBERATELY NON-COMMITTAL — "if that email has an account". The
              server returns an identical response whether or not the address is registered,
              precisely so this form cannot be used to discover who has an account. Wording this as
              "We've sent you an email" would leak exactly what the server refused to. */}
          {panel === 'forgot_sent' && (
            <div className="mt-6 grid gap-4">
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                The link expires in 30 minutes. If it does not arrive, check your spam folder — or
                ask your InCruiter account manager whether this address has a dashboard login.
              </p>
              <BackToSignIn onPanelChange={onPanelChange} />
            </div>
          )}

          {/* ── panel: CHOOSE A NEW PASSWORD ──────────────────────────────── */}
          {panel === 'reset' && (
            <form onSubmit={onSubmitReset} className="mt-6 grid gap-4">
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                Choose a new password for your account. It must be at least 8 characters.
              </p>
              <div className="grid gap-2">
                <Label htmlFor="reset-password">New password</Label>
                <Input id="reset-password" type="password" required autoFocus minLength={8}
                       autoComplete="new-password" placeholder="••••••••"
                       value={password} onChange={(e) => onPasswordChange(e.target.value)} />
              </div>
              <div className="grid gap-2">
                {/* Asked twice on purpose: a typo entered once locks the person out of the very
                    account they are recovering, and the next recovery attempt needs another email. */}
                <Label htmlFor="reset-confirm">Confirm new password</Label>
                <Input id="reset-confirm" type="password" required minLength={8}
                       autoComplete="new-password" placeholder="••••••••"
                       value={confirmPassword}
                       onChange={(e) => onConfirmPasswordChange(e.target.value)} />
              </div>
              <Button type="submit" size="lg" disabled={busy}
                      className="mt-1 w-full h-11 text-[14px] font-semibold">
                {busy ? 'Updating…' : 'Update password'}
              </Button>
              <BackToSignIn onPanelChange={onPanelChange} />
            </form>
          )}

          {/* Google sits under the SIGN-IN panel only — it is a way in, not a way to recover.
              It never renders on the staff door: /auth/providers?door=admin reports it disabled,
              and the endpoint refuses a Google credential there regardless. */}
          {googleReady && panel === 'signin' && (
            <>
              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  or
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
              {/* Google renders its own button into this node; it cannot be restyled, which is why
                  it sits below the divider rather than beside our own controls. */}
              <div ref={googleRef} className="flex justify-center" />
              <p className="mt-3 text-center text-[11.5px] text-muted-foreground">
                {selfRegistration
                  ? 'New here? Continuing with Google creates your account. '
                    + 'An InCruiter administrator then activates it and adds your credits.'
                  : 'Google sign-in works only for accounts already registered for the dashboard.'}
              </p>
            </>
          )}

          <p className="mt-8 text-center text-[12.5px] text-muted-foreground">
            {isAdminDoor
              ? 'InCruiter staff only. Client accounts sign in on the client dashboard.'
              : 'Trouble signing in? Contact your InCruiter account manager.'}
          </p>
        </div>
      </div>
    </div>
  )
}

/** [2026-08-27] ADDED — the way back from the forgot/reset panels.
 *
 *  A real <button>, not an anchor: it changes what this page RENDERS, it does not navigate. An
 *  `<a href="#">` here would push a history entry and put a stray `#` in the address bar of a page
 *  someone may be about to be emailed a link for. */
function BackToSignIn({ onPanelChange }) {
  return (
    <button type="button" onClick={() => onPanelChange('signin')}
            className="mx-auto text-[12.5px] font-medium text-muted-foreground
                       hover:text-foreground cursor-pointer appearance-none border-0
                       bg-transparent p-0 [font-family:inherit]
                       focus-visible:outline-none focus-visible:underline">
      Back to sign in
    </button>
  )
}

/** The left panel. Customer artwork when present, a branded gradient when not.
 *
 *  The gradient is the BACKGROUND of this element rather than a separate node, so it is also what
 *  shows through if an image is present but still decoding — the panel is never white, and the
 *  white caption text over it is never briefly invisible. */
function CoverPanel() {
  const [failed, setFailed] = useState(false)
  const showImage = COVER_SRC && !failed

  return (
    <div className="relative hidden lg:block overflow-hidden"
         style={{ background: 'linear-gradient(150deg, #0d366b 0%, #184f95 45%, #2a78d6 100%)' }}>

      {showImage && (
        <img
          src={COVER_SRC}
          alt=""
          /* Decorative: the panel carries no information the form does not. An alt description
             here would just be noise read aloud before the fields that matter. */
          aria-hidden="true"
          /* Belt and braces. The glob means the file existed at BUILD time; this covers it being
             removed from the served bundle afterwards, so a stale deploy degrades to the gradient
             rather than showing a broken-image icon on the product's front door. */
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      {/* Scrim — NEUTRAL BLACK, not the navy it started as.
          The caption is white and has to stay legible over an image this component has never
          seen, so a scrim is not optional. But a blue-tinted one greys out any warm image: with
          the current orange cover it turned the fire muddy brown. Neutral black darkens without
          shifting hue, so a warm image stays warm and a cool one stays cool.
          Weighted to the BOTTOM, which is the only place text sits. */}
      <div className="absolute inset-0"
           style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 35%, rgba(0,0,0,0) 70%)' }} />

      {/* Caption only — the wordmark lives in the form column. See the note there. */}
      <div className="relative flex h-full flex-col justify-end p-12">
        <div className="max-w-[420px]">
          <p className="text-[22px] font-semibold leading-snug text-white">
            Proctoring that stands up to scrutiny.
          </p>
          <p className="mt-3 text-[14px] leading-relaxed text-white/75">
            Recorded-video analysis and live browser signals for the same candidate, in one report —
            with every finding traceable to the evidence behind it.
          </p>
        </div>
      </div>
    </div>
  )
}
